import { useEffect, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { CommunityQuestionPicker } from '../../../../pickers/community-question/CommunityQuestionPicker';
import type { CommunityQuestionBase } from '../../../Community/types/community-questions';
import type { JourneyItemTemplateCommunityQuestionFirestore } from '../../services/journeyTypes';
import { JourneyItemFormActions } from './JourneyItemFormActions';
import { JourneyItemFormFields } from './JourneyItemFormFields';
import { useJourneyItemForm } from './useJourneyItemForm';

interface CommunityQuestionSpecificFormData {
    questionId: string;
}

interface CommunityQuestionJourneyItemFormProps {
    itemTemplate?: JourneyItemTemplateCommunityQuestionFirestore;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateCommunityQuestionFirestore) => void;
    onSaveAndContinue?: (item: JourneyItemTemplateCommunityQuestionFirestore) => void;
    onCancel: () => void;
}

export const CommunityQuestionJourneyItemForm = ({ itemTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: CommunityQuestionJourneyItemFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue,
    } = useJourneyItemForm({
        itemTemplate,
        initialOrder,
        defaultProgressType: 'fixed',
    });

    const [specificFormData, setSpecificFormData] = useState<CommunityQuestionSpecificFormData>({
        questionId: '',
    });

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setSpecificFormData({
                questionId: itemTemplate.questionId,
            });
        }
    }, [itemTemplate]);

    const handleChange = (field: string, value: string | number | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value);
        } else {
            setSpecificFormData((prev) => ({ ...prev, [field]: value }));
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.questionId.trim()) {
            return 'Question is required for community question items';
        }
        return null;
    };

    const createItem = (baseData: Partial<JourneyItemTemplateCommunityQuestionFirestore>): JourneyItemTemplateCommunityQuestionFirestore => {
        return {
            ...baseData,
            type: 'community-question',
            maxProgress: 1,
            questionId: specificFormData.questionId,
        } as JourneyItemTemplateCommunityQuestionFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createItem, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createItem, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormFields
                formData={commonFormData}
                itemType="community-question"
                disableProgressType={true}
                onChange={handleChange}
            />

            <h2 className="text-lg font-bold mt-2">Community Question-specific fields</h2>
            <hr className="-mt-4" />

            <CommunityQuestionPicker
                value={specificFormData.questionId}
                onChange={(question: CommunityQuestionBase | null) => {
                    if (question) {
                        setSpecificFormData((prev) => ({
                            ...prev,
                            questionId: question.id,
                        }));
                    } else {
                        setSpecificFormData((prev) => ({ ...prev, questionId: '' }));
                    }
                }}
            />

            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!itemTemplate?.id}
            />
        </form>
    );
};
